import BaseCheckMarkIcon from '../../../../../../assets/icons/checkmark.svg?react';
import { numberOfRoadDataByRubricIdSelector } from '../../../../../../core/legacy/selectors/streetView';
import { theme } from '../../../../style/theme';
import {
    FilterRubricMainText,
    FilterRubricExtraText,
    FilterRubricTextContainer,
    StyledFilterRubric,
    FilterRubricOpen,
} from './styles';
import { useMemo, useState, FC } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../types';
import { rubricToDisplayIdsQuery } from '../../../../../../core/filters/queries';
import { ToggleRubricToDisplayIdCommand } from '../../../../../../core/filters/commands/ToggleRubricToDisplayIdCommand';
import type { Rubric } from '../../../../../../core/domain/Roaddata';
import FilterReferenceGroup from './FilterReferenceGroup';
import FilterReference from './FilterReference';
import { Stack } from '@mui/material';

const CheckMarkIcon = () => <BaseCheckMarkIcon color={theme.color.white.main} />;

type FilterRubricProps = {
    rubric: Rubric;
};

const FilterRubric: FC<FilterRubricProps> = ({ rubric }) => {
    const dispatch = useAppDispatch();
    const rubricToDisplayIds = useAppSelector(rubricToDisplayIdsQuery);
    const numberOfRoadDataForThisRubric = useAppSelector(numberOfRoadDataByRubricIdSelector(rubric.id));
    const selected = rubricToDisplayIds.includes(rubric.id);

    const [open, setOpen] = useState(false);

    const { parentRefs, childrenByParent, orphanRefs } = useMemo(() => {
        const withoutParent = rubric.references.filter((r) => !r.parentId);

        const byParent = new Map<string, typeof rubric.references>();
        rubric.references
            .filter((r) => r.parentId)
            .forEach((r) => {
                const existing = byParent.get(r.parentId!) || [];
                existing.push(r);
                byParent.set(r.parentId!, existing);
            });

        // No parent-child hierarchy at all: fall back to a flat sorted list
        if (byParent.size === 0) {
            return {
                parentRefs: [],
                childrenByParent: new Map(),
                orphanRefs: [...rubric.references].sort((a, b) => a.name.localeCompare(b.name)),
            };
        }

        // Real parents (have children) always render above standalone refs
        // (no parent, no children — e.g. "Autre", "Nid de poule"), which sort
        // to the bottom instead of being interleaved alphabetically with them.
        const parents = withoutParent
            .filter((r) => byParent.has(r.id))
            .sort((a, b) => a.key.localeCompare(b.key));
        const orphans = withoutParent
            .filter((r) => !byParent.has(r.id))
            .sort((a, b) => a.name.localeCompare(b.name));

        return { parentRefs: parents, childrenByParent: byParent, orphanRefs: orphans };
    }, [rubric.references]);

    const handleClick = () => {
        dispatch(ToggleRubricToDisplayIdCommand(rubric));
    };

    const rubricNameColor = selected ? theme.color.white.main : theme.color.black.dark;
    const rubricCountColor = selected ? theme.color.white.main : theme.color.grey.main;

    return (
        <>
            <Stack direction="row" alignItems="center" sx={{ position: 'relative' }}>
                {rubric.references.length > 0 && (
                    <FilterRubricOpen onClick={() => setOpen(!open)}>{open ? '-' : '+'}</FilterRubricOpen>
                )}
                <StyledFilterRubric selected={selected} onClick={handleClick}>
                    <FilterRubricTextContainer>
                        <FilterRubricMainText color={rubricNameColor}>{rubric.name}</FilterRubricMainText>
                        <FilterRubricExtraText color={rubricCountColor}>
                            {numberOfRoadDataForThisRubric}
                        </FilterRubricExtraText>
                    </FilterRubricTextContainer>
                    {selected && <CheckMarkIcon />}
                </StyledFilterRubric>
            </Stack>
            {open &&
                parentRefs.length > 0 &&
                parentRefs.map((parent) => (
                    <FilterReferenceGroup
                        key={parent.id}
                        parent={parent}
                        children={childrenByParent.get(parent.id) || []}
                        rubric={rubric}
                    />
                ))}
            {open &&
                orphanRefs.length > 0 &&
                orphanRefs.map((r) => <FilterReference key={r.id} reference={r} rubric={rubric} />)}
        </>
    );
};

export default FilterRubric;
